import React from 'react';

const Chapter4Content = () => {
  return (
    <article className="prose prose-lg max-w-none">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Chapter Four</h1>
        <p className="text-xl text-gray-600">The Border Pieces</p>
        <p className="text-lg text-gray-500 mt-2">Six Passages That Begin to Build the Frame from the Corner Pieces</p>
        <div className="flex justify-center gap-4 mt-4">
          <span className="text-2xl text-amber-600">✦</span>
          <span className="text-2xl text-amber-600">✦</span>
          <span className="text-2xl text-amber-600">✦</span>
        </div>
      </div>

      <div className="mb-12">
        <p className="text-gray-800 leading-relaxed mb-4">
          Border pieces have one straight edge. They connect to the frame the corners established — locking everything together and closing the picture. Here are six border pieces that seal the case.
        </p>
      </div>

      {/* BORDER PIECE ONE */}
      <div className="mb-16">
        <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-6">
          <h2 className="text-2xl font-bold text-blue-900 mb-2">Border Piece One — Revelation 11:15, 18</h2>
        </div>

        <p className="text-gray-800 leading-relaxed mb-4">
          The seventh trumpet of Revelation and the last trumpet of 1 Corinthians 15 are not two different events. They are one event, described twice. Compare them side by side:
        </p>

        <div className="overflow-x-auto my-8">
          <table className="w-full border-2 border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-4 text-left font-bold border-r border-gray-300">1 Corinthians 15 — The Last Trumpet</th>
                <th className="p-4 text-left font-bold">Revelation 11 — The Seventh Trumpet</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-gray-300">
                <td className="p-4 border-r border-gray-300">The dead shall be raised</td>
                <td className="p-4">The time of the dead, to be judged</td>
              </tr>
              <tr className="border-t border-gray-300">
                <td className="p-4 border-r border-gray-300">We shall be changed — reward given</td>
                <td className="p-4">Reward Your servants</td>
              </tr>
              <tr className="border-t border-gray-300">
                <td className="p-4 border-r border-gray-300">Death is destroyed</td>
                <td className="p-4">"It is done!"</td>
              </tr>
              <tr className="border-t border-gray-300">
                <td className="p-4 border-r border-gray-300">The Kingdom comes</td>
                <td className="p-4">The kingdoms become the Lord's</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-gray-800 leading-relaxed mb-4 font-semibold text-center">
          They are not similar. They are identical, point for point. One announcement. One description. Same moment. Same God. Same plan.
        </p>

        {/* GIT Table */}
        <div className="my-12">
          <h3 className="text-2xl font-bold text-center text-amber-700 mb-6">GRAMMAR INTEGRITY TEST (G.I.T.) — The Legend</h3>
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-6">
            <h4 className="font-bold text-lg text-center mb-4">Border Piece One — Revelation 11:15, 18</h4>
            <p className="text-center italic text-gray-700 mb-6">
              "The kingdoms of this world are become the kingdoms of our Lord... and the time of the dead, that they should be judged, and that thou shouldest give reward unto thy servants..." — Revelation 11:15, 18
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-amber-100">
                    <th className="p-3 text-left font-bold">GRAMMAR ELEMENT</th>
                    <th className="p-3 text-left font-bold">WHAT THE TEXT ACTUALLY SAYS</th>
                    <th className="p-3 text-left font-bold">WHAT THIS TELLS US</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-amber-200">
                    <td className="p-3 font-semibold align-top">SUBJECT<br/>(Who is this about?)</td>
                    <td className="p-3 align-top italic">"The kingdoms of this world" and "thy servants" — the world being reclaimed and the saints being rewarded. The dead being raised are the same "we" Paul speaks of in 1 Corinthians 15.</td>
                    <td className="p-3 align-top">The subject is God's kingdom coming and God's people being rewarded. This is the culmination, not a preliminary event. The dead are raised. The servants receive their reward. This is the same gathering Paul describes.</td>
                  </tr>
                  <tr className="border-t border-amber-200">
                    <td className="p-3 font-semibold align-top">VERB<br/>(What does it do?)</td>
                    <td className="p-3 align-top italic">"Are become" / "give reward" / "the time of the dead, to be judged" — a completed, decisive transfer of authority. The trumpet sounds and it is done.</td>
                    <td className="p-3 align-top">The seventh trumpet does not announce more delay — it announces completion. The kingdoms transfer. The dead are raised. The saints are rewarded. This is the same transformation Paul calls the last trump event.</td>
                  </tr>
                  <tr className="border-t border-amber-200">
                    <td className="p-3 font-semibold align-top">OBJECT / TIMELINE<br/>(When? Under what condition?)</td>
                    <td className="p-3 align-top italic">"The seventh angel sounded" — the seventh and final trumpet in Revelation's sequence. After this there is no eighth. This is the last trumpet in the sequence of human history.</td>
                    <td className="p-3 align-top">If the last trumpet of 1 Corinthians 15 is the same as the seventh trumpet of Revelation — and the identical language confirms it — then the gathering happens at the end of the trumpet sequence, not before it begins.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 bg-white border-2 border-amber-400 rounded-lg p-4">
              <h4 className="text-center font-bold text-amber-800 mb-2 flex items-center justify-center gap-2">
                <span className="text-xl">✦</span>
                VERDICT
                <span className="text-xl">✦</span>
              </h4>
              <p className="text-center text-gray-800">
                The seventh trumpet of Revelation 11 and the last trumpet of 1 Corinthians 15 are the same event. Point-for-point correspondence: dead raised, servants rewarded, kingdoms transfer, death defeated. One arrival. One trumpet. One moment. The Border locks with Corner Three.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* BORDER PIECE TWO */}
      <div className="mb-16">
        <div className="bg-green-50 border-l-4 border-green-600 p-6 mb-6">
          <h2 className="text-2xl font-bold text-green-900 mb-2">Border Piece Two — 1 Thessalonians 4:17</h2>
        </div>

        <p className="text-gray-800 leading-relaxed mb-4">
          "We shall be caught up together... to meet the Lord in the air."
        </p>

        <p className="text-gray-800 leading-relaxed mb-4">
          That word <em>meet</em> — <strong>apantesis</strong> in Greek — was a specific word used in the ancient world for a specific kind of event. When a king or dignitary approached a city, the citizens would go out of the city to meet him on the road. They would form a procession, greet him, and then escort him back into the city in triumph. They did not leave forever. They gave him a royal welcome.
        </p>

        <p className="text-gray-800 leading-relaxed mb-4">
          This is exactly what the people did when Jesus rode in on a donkey as the lamb of God. But next time he comes on a war Horse as a King.
        </p>

        <p className="text-gray-800 leading-relaxed mb-4">
          Think of it this way. On valentines day 1997 the Lord took me up in visions. One of the visions, many were standing with a shofar, a trumpet, at the entrance of a great Manison. Like a Castle. Many others were over the hill escorting The King to the Castle. I was one of those with the King escorting and sounding the Trumpet. We were escorting our King to earth. To His Palace.
        </p>

        <p className="text-gray-800 leading-relaxed mb-4">
          This word <em>apantesis</em> "Meet" Paul uses for the gathering. We go up to meet the Lord in the air — and then we escort Him back to earth. We are not evacuees on the last flight out. We are the honor guard of the returning King. Not less glorious — more. That's why I call the Kingdom Escort Guard those that escort the King with the Shofar and those that are lined up in the entry of the castle to sound also like a great orchestra of 120 shofar blowers in perfect accord when we come into view with our King.
        </p>

        <p className="text-gray-800 leading-relaxed mb-4 font-semibold">
          That vision was not just for me. It was the preview of who you are: Kingdom Escort Guard, shofar in hand, lining the road to escort the returning King.
        </p>

        {/* GIT Table for Border Piece Two */}
        <div className="my-12">
          <h3 className="text-2xl font-bold text-center text-amber-700 mb-6">GRAMMAR INTEGRITY TEST (G.I.T.) — The Legend</h3>
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-6">
            <h4 className="font-bold text-lg text-center mb-4">Border Piece Two — 1 Thessalonians 4:17</h4>
            <p className="text-center italic text-gray-700 mb-6">
              "Then we who are alive, who are left, will be caught up together with them in the clouds to meet the Lord in the air, and so we will always be with the Lord." — 1 Thessalonians 4:17
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-amber-100">
                    <th className="p-3 text-left font-bold">GRAMMAR ELEMENT</th>
                    <th className="p-3 text-left font-bold">WHAT THE TEXT ACTUALLY SAYS</th>
                    <th className="p-3 text-left font-bold">WHAT THIS TELLS US</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-amber-200">
                    <td className="p-3 font-semibold align-top">SUBJECT<br/>(Who is this about?)</td>
                    <td className="p-3 align-top italic">"We who are alive and remain" — living believers at the moment of the Lord's return, along with the dead in Christ who rise first (v.16).</td>
                    <td className="p-3 align-top">The subject is the full company of the redeemed — the dead resurrected and the living transformed — gathered together as one body to escort the King. These are not evacuees. These are the honor guard of the returning King.</td>
                  </tr>
                  <tr className="border-t border-amber-200">
                    <td className="p-3 font-semibold align-top">VERB<br/>(What does it do?)</td>
                    <td className="p-3 align-top italic">"Caught up" (harpazo) / "to meet" (apantēsis) — the gathering is forceful, swift, and purposeful. Apantēsis is a royal welcome formation: citizens go out to meet the approaching dignitary and escort him back.</td>
                    <td className="p-3 align-top">The direction of the movement is the key: we go up to meet Him in order to escort Him back. This is not an evacuation to a distant place. This is the Kingdom Escort Guard forming up to bring the King home to earth.</td>
                  </tr>
                  <tr className="border-t border-amber-200">
                    <td className="p-3 font-semibold align-top">OBJECT / TIMELINE<br/>(When? Under what condition?)</td>
                    <td className="p-3 align-top italic">"And so we will always be with the Lord" — the destination is wherever the Lord is going. And where is He going? Back to earth. The apantēsis procession always returns with the dignitary.</td>
                    <td className="p-3 align-top">The timing is confirmed by the sequence: v.16 — the dead in Christ rise first, v.17 — the living are caught up together with them. This happens at the trumpet. The same last trumpet. The same post-tribulation moment.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 bg-white border-2 border-amber-400 rounded-lg p-4">
              <h4 className="text-center font-bold text-amber-800 mb-2 flex items-center justify-center gap-2">
                <span className="text-xl">✦</span>
                VERDICT
                <span className="text-xl">✦</span>
              </h4>
              <p className="text-center text-gray-800">
                The word apantēsis reveals the nature of the Gathering: we are not leaving earth forever — we are forming up as the honor guard of the returning King. We go up to meet Him and escort Him back. Not evacuees. The royal escort of the King of Kings, coming to take His throne.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* BORDER PIECE THREE */}
      <div className="mb-16">
        <div className="bg-purple-50 border-l-4 border-purple-600 p-6 mb-6">
          <h2 className="text-2xl font-bold text-purple-900 mb-2">Border Piece Three — 2 Thessalonians 1:7–8</h2>
        </div>

        <p className="text-gray-800 leading-relaxed mb-4">
          "To give you who are troubled rest with us when the Lord Jesus is revealed from heaven with His mighty angels, in flaming fire."
        </p>

        <p className="text-gray-800 leading-relaxed mb-4">
          Paul is writing to believers who are suffering — pressured, persecuted, troubled. And he promises them rest. When? When Jesus is revealed in flaming fire. Not seven years before. Not at a quiet pre-dawn departure. When He comes in flaming fire.
        </p>

        <p className="text-gray-800 leading-relaxed mb-4 font-semibold">
          The rest and the fire are the same moment. No gap. One arrival. Two simultaneous results.
        </p>

        {/* GIT Table for Border Piece Three */}
        <div className="my-12">
          <h3 className="text-2xl font-bold text-center text-amber-700 mb-6">GRAMMAR INTEGRITY TEST (G.I.T.) — The Legend</h3>
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-6">
            <h4 className="font-bold text-lg text-center mb-4">Border Piece Three — 2 Thessalonians 1:7–8</h4>
            <p className="text-center italic text-gray-700 mb-6">
              "To give you who are troubled rest with us, when the Lord Jesus is revealed from heaven with His mighty angels in flaming fire..." — 2 Thessalonians 1:7–8
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-amber-100">
                    <th className="p-3 text-left font-bold">GRAMMAR ELEMENT</th>
                    <th className="p-3 text-left font-bold">WHAT THE TEXT ACTUALLY SAYS</th>
                    <th className="p-3 text-left font-bold">WHAT THIS TELLS US</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-amber-200">
                    <td className="p-3 font-semibold align-top">SUBJECT<br/>(Who is this about?)</td>
                    <td className="p-3 align-top italic">"You who are troubled" — believers in Thessalonica who are suffering persecution. Paul addresses them directly. They are real people enduring real pressure right now.</td>
                    <td className="p-3 align-top">The subject is persecuted believers — the very people who in the pretribulation theory would have already been raptured before trouble began. But Paul is writing to troubled believers and promising them rest at the return. They are still here. They are still enduring.</td>
                  </tr>
                  <tr className="border-t border-amber-200">
                    <td className="p-3 font-semibold align-top">VERB<br/>(What does it do?)</td>
                    <td className="p-3 align-top italic">"Give rest" — the promise is specific and conditional. Relief comes at a specific moment, not before it.</td>
                    <td className="p-3 align-top">The action is the giving of rest — deliverance, relief, the lifting of the burden of persecution. The verb is tied to a specific event. The rest does not come early. It comes when Jesus arrives.</td>
                  </tr>
                  <tr className="border-t border-amber-200">
                    <td className="p-3 font-semibold align-top">OBJECT / TIMELINE<br/>(When? Under what condition?)</td>
                    <td className="p-3 align-top italic">"When the Lord Jesus is revealed from heaven with His mighty angels, in flaming fire" — the moment of rest is the moment of revelation. Apokalypsis: an unveiling, a disclosure, the moment He appears in glory.</td>
                    <td className="p-3 align-top">The rest and the flaming fire are simultaneous. There is no gap between the believers' rest and the Lord's fiery revelation. One arrival. Two simultaneous results: the saved receive rest and the wicked receive judgment — at the same moment, from the same King.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 bg-white border-2 border-amber-400 rounded-lg p-4">
              <h4 className="text-center font-bold text-amber-800 mb-2 flex items-center justify-center gap-2">
                <span className="text-xl">✦</span>
                VERDICT
                <span className="text-xl">✦</span>
              </h4>
              <p className="text-center text-gray-800">
                Paul promises suffering believers rest at the revealing of Christ in flaming fire. The pretribulation position places their rest years before the fire. But Paul places both at the same moment. The border piece is plain: deliverance and judgment arrive together, at the same coming.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* BORDER PIECE FOUR */}
      <div className="mb-16">
        <div className="bg-red-50 border-l-4 border-red-600 p-6 mb-6">
          <h2 className="text-2xl font-bold text-red-900 mb-2">Border Piece Four — 2 Thessalonians 2:3, 8</h2>
        </div>

        <p className="text-gray-800 leading-relaxed mb-4">
          Corner Piece One told us the gathering cannot happen until the Antichrist is revealed. Now here is what it did not yet tell us — what happens to that Antichrist:
        </p>

        <p className="text-gray-800 leading-relaxed mb-4">
          "The man of sin... whom the Lord shall destroy with the brightness of His coming."
        </p>

        <p className="text-gray-800 leading-relaxed mb-4 font-semibold">
          The same coming — the same parousia — that gathers the saints is the one that destroys the Antichrist. Paul uses one word for both events in the same passage. One arrival. Two results. The saved are gathered and the wicked are destroyed at the same moment by the same King.
        </p>

        {/* GIT Table for Border Piece Four */}
        <div className="my-12">
          <h3 className="text-2xl font-bold text-center text-amber-700 mb-6">GRAMMAR INTEGRITY TEST (G.I.T.) — The Legend</h3>
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-6">
            <h4 className="font-bold text-lg text-center mb-4">Border Piece Four — 2 Thessalonians 2:3, 8</h4>
            <p className="text-center italic text-gray-700 mb-6">
              "The man of sin... whom the Lord shall consume with the spirit of his mouth, and shall destroy with the brightness of his coming." — 2 Thessalonians 2:3, 8
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-amber-100">
                    <th className="p-3 text-left font-bold">GRAMMAR ELEMENT</th>
                    <th className="p-3 text-left font-bold">WHAT THE TEXT ACTUALLY SAYS</th>
                    <th className="p-3 text-left font-bold">WHAT THIS TELLS US</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-amber-200">
                    <td className="p-3 font-semibold align-top">SUBJECT<br/>(Who is this about?)</td>
                    <td className="p-3 align-top italic">"The man of sin" (v.3) and "the Lord" (v.8) — the two principals in the final confrontation.</td>
                    <td className="p-3 align-top">Corner Piece One named the Antichrist as the precondition of the Gathering — he must be revealed first. Now that same Antichrist is destroyed by the same arrival that gathers the saints. Paul names both principals in the same passage to make the connection unmistakable.</td>
                  </tr>
                  <tr className="border-t border-amber-200">
                    <td className="p-3 font-semibold align-top">VERB<br/>(What does it do?)</td>
                    <td className="p-3 align-top italic">"Consume with the spirit of his mouth" and "destroy with the brightness of his coming" — two expressions for the same decisive act. The Antichrist is ended at the moment of Christ's arrival.</td>
                    <td className="p-3 align-top">The verb is destruction — final, complete, irreversible. And it happens at the parousia, the same word Paul used in verse 1 for the coming of our Lord Jesus Christ. The arrival that gathers the saints is the same arrival that destroys the Antichrist.</td>
                  </tr>
                  <tr className="border-t border-amber-200">
                    <td className="p-3 font-semibold align-top">OBJECT / TIMELINE<br/>(When? Under what condition?)</td>
                    <td className="p-3 align-top italic">"The brightness of his coming" (parousia) — the same Greek word used in v.1 for the coming tied to the Gathering. Paul binds these two results — gathering and destruction — to the same word, the same event.</td>
                    <td className="p-3 align-top">One parousia. Two simultaneous results. A pretribulation gathering requires the parousia to be split into two separate events, years apart. But Paul uses one word for one coming. The text does not allow the split.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 bg-white border-2 border-amber-400 rounded-lg p-4">
              <h4 className="text-center font-bold text-amber-800 mb-2 flex items-center justify-center gap-2">
                <span className="text-xl">✦</span>
                VERDICT
                <span className="text-xl">✦</span>
              </h4>
              <p className="text-center text-gray-800">
                The parousia "coming" that gathers the Church is the same parousia that destroys the Antichrist. Paul deliberately uses one word for both in the same passage. One arrival. One brightness. Two results: the redeemed gathered and the wicked destroyed — at the same moment.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* BORDER PIECE FIVE */}
      <div className="mb-16">
        <div className="bg-indigo-50 border-l-4 border-indigo-600 p-6 mb-6">
          <h2 className="text-2xl font-bold text-indigo-900 mb-2">Border Piece Five — John 5:28–29</h2>
        </div>

        <p className="text-gray-800 leading-relaxed mb-4">
          "The hour is coming in which all who are in the graves will hear His voice and come forth — those who have done good, to the resurrection of life, and those who have done evil, to the resurrection of condemnation."
        </p>

        <p className="text-gray-800 leading-relaxed mb-4 font-semibold">
          Jesus draws the complete map of the resurrections. Two kinds: life and condemnation. Not three. Two.
        </p>

        <p className="text-gray-800 leading-relaxed mb-4">
          A pre-tribulation system requires two separate resurrections of the righteous — one for the Church before the tribulation, one for the martyrs after it. But Jesus says two kinds: one for the righteous, one for the wicked. The math does not work any other way.
        </p>

        {/* GIT Table for Border Piece Five */}
        <div className="my-12">
          <h3 className="text-2xl font-bold text-center text-amber-700 mb-6">GRAMMAR INTEGRITY TEST (G.I.T.) — The Legend</h3>
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-6">
            <h4 className="font-bold text-lg text-center mb-4">Border Piece Five — John 5:28–29</h4>
            <p className="text-center italic text-gray-700 mb-6">
              "The hour is coming in which all who are in the graves will hear His voice and come forth — those who have done good, to the resurrection of life, and those who have done evil, to the resurrection of condemnation." — John 5:28–29
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-amber-100">
                    <th className="p-3 text-left font-bold">GRAMMAR ELEMENT</th>
                    <th className="p-3 text-left font-bold">WHAT THE TEXT ACTUALLY SAYS</th>
                    <th className="p-3 text-left font-bold">WHAT THIS TELLS US</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-amber-200">
                    <td className="p-3 font-semibold align-top">SUBJECT<br/>(Who is this about?)</td>
                    <td className="p-3 align-top italic">"All who are in the graves" — the complete company of the dead, both righteous and wicked. Jesus draws no distinction of dispensation, nationality, or era.</td>
                    <td className="p-3 align-top">The subject is every human being who has ever died. Jesus addresses the totality of the resurrection in a single statement. He says all — and he divides them into two groups, not three.</td>
                  </tr>
                  <tr className="border-t border-amber-200">
                    <td className="p-3 font-semibold align-top">VERB<br/>(What does it do?)</td>
                    <td className="p-3 align-top italic">"Will hear His voice and come forth" — the active agent is Jesus. His voice is the mechanism of resurrection.</td>
                    <td className="p-3 align-top">The action is universal resurrection triggered by one voice. Jesus calls once. All respond — some to life, some to condemnation. One calling event with two results, not multiple calling events separated by years.</td>
                  </tr>
                  <tr className="border-t border-amber-200">
                    <td className="p-3 font-semibold align-top">OBJECT / TIMELINE<br/>(When? Under what condition?)</td>
                    <td className="p-3 align-top italic">"The hour is coming" — one hour. Not multiple hours. Not a seven-year gap between the first group and the second. One hour in which all the dead come forth.</td>
                    <td className="p-3 align-top">"The hour" is singular. Jesus places both resurrections in the same hour. A pretribulation position requires two separate righteous resurrections. That would require three categories across the hour. But Jesus says two groups in one hour. The math does not work any other way.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 bg-white border-2 border-amber-400 rounded-lg p-4">
              <h4 className="text-center font-bold text-amber-800 mb-2 flex items-center justify-center gap-2">
                <span className="text-xl">✦</span>
                VERDICT
                <span className="text-xl">✦</span>
              </h4>
              <p className="text-center text-gray-800">
                Jesus gives the complete resurrection map: one hour, two groups, one voice. No gap of seven years between righteous resurrections. One hour — all the dead come forth. The pretribulation system requires a split that Jesus himself does not allow.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* BORDER PIECE SIX */}
      <div className="mb-16">
        <div className="bg-teal-50 border-l-4 border-teal-600 p-6 mb-6">
          <h2 className="text-2xl font-bold text-teal-900 mb-2">Border Piece Six — Revelation 16:15</h2>
          <p className="text-sm text-teal-700 mt-2 font-semibold">The most clear word of Jesus of when the Rapture occurs.</p>
        </div>

        <p className="text-gray-800 leading-relaxed mb-4">
          Now I want you to picture the scene. The world is at the absolute climax of everything. The six bowls have been poured out. Demonic spirits have gathering armies for the last battle of history at Armageddon. Everything is building to the final moment.
        </p>

        <p className="text-gray-800 leading-relaxed mb-4">
          And right in the middle of it all — right at the most intense, most final moment of the tribulation — Jesus suddenly speaks. He interrupts the narrative. He speaks directly to the reader:
        </p>

        <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-400 rounded-lg p-6 my-8">
          <p className="text-lg font-semibold text-center text-gray-900">
            "Behold, I come as a thief.<br/>
            Blessed is he that watcheth, and keepeth his garments,<br/>
            lest he walk naked, and they see his shame."
          </p>
          <p className="text-sm text-gray-700 font-semibold text-center mt-4">— Revelation 16:15</p>
        </div>

        <p className="text-gray-800 leading-relaxed mb-4 font-semibold">
          This Word from Jesus is placed right in the Armageddon battle, after the six the seal, trumpet and bowl. Before the seventh seal, trumpet and bowl!
        </p>

        <p className="text-gray-800 leading-relaxed mb-4">
          Every one knows these words are the words of the gathering the rapture through all the new testament.
        </p>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 my-8">
          <p className="text-blue-900 leading-relaxed italic mb-4">
            "The sixth angel poured out his bowl on the great river Euphrates, and its water was dried up, to prepare the way for the kings from the east. And I saw, coming out of the mouth of the dragon and out of the mouth of the beast and out of the mouth of the false prophet, three unclean spirits like frogs. For they are demonic spirits, performing signs, who go abroad to the kings of the whole world, to assemble them for battle on the great day of God the Almighty.
          </p>
          <p className="text-blue-900 leading-relaxed italic mb-4">
            ("Behold, I am coming like a thief! Blessed is the one who stays awake, keeping his garments on, that he may not go about naked and be seen exposed!")
          </p>
          <p className="text-blue-900 leading-relaxed italic">
            And they assembled them at the place that in Hebrew is called Armageddon. The seventh angel poured out his bowl into the air, and a loud voice came out of the temple, from the throne, saying, "It is done!"
          </p>
          <p className="text-sm text-blue-700 font-semibold text-right mt-4">— Revelation 16:12-17</p>
        </div>

        <p className="text-gray-800 leading-relaxed mb-4">
          Jesus says "I come as a thief" — inside end of the Armageddon event. He says "Blessed is he who watches" — at this moment, on the last page of the tribulation.
        </p>

        <p className="text-gray-800 leading-relaxed mb-4">
          He is talking to people who are there. On the earth. At that moment. His people — present, watching, enduring — at the very end. Many who have been hidden from tribulation. I like to call them the Amish and believers that have farms and do not even need the mark of the beast to buy or sell to feed themselves. They are to me the quintessential meaning of the Philadelphia church.
        </p>

        <p className="text-gray-800 leading-relaxed mb-4 font-semibold">
          Jesus placed a rapture warning inside Armageddon. His people are still here.
        </p>

        <p className="text-gray-800 leading-relaxed mb-4 font-semibold text-lg">
          Once you see it, you cannot unsee it. The frame is complete enough. You can put the rest together!
        </p>

        {/* GIT Table for Border Piece Six */}
        <div className="my-12">
          <h3 className="text-2xl font-bold text-center text-amber-700 mb-6">GRAMMAR INTEGRITY TEST (G.I.T.) — The Legend</h3>
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-6">
            <h4 className="font-bold text-lg text-center mb-4">Border Piece Six — Revelation 16:15</h4>
            <p className="text-center italic text-gray-700 mb-6">
              "Behold, I come as a thief. Blessed is he that watcheth, and keepeth his garments..." — Revelation 16:15
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-amber-100">
                    <th className="p-3 text-left font-bold">GRAMMAR ELEMENT</th>
                    <th className="p-3 text-left font-bold">WHAT THE TEXT ACTUALLY SAYS</th>
                    <th className="p-3 text-left font-bold">WHAT THIS TELLS US</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-amber-200">
                    <td className="p-3 font-semibold align-top">SUBJECT<br/>(Who is this about?)</td>
                    <td className="p-3 align-top italic">"I" (Jesus speaking directly) and "he that watcheth" (the faithful believer on earth). Jesus inserts Himself into the Armageddon sequence to address His people who are present at that moment.</td>
                    <td className="p-3 align-top">The subject is Jesus and His watching people — believers alive and present during the final bowl sequence. If the Church had been raptured before the tribulation, Jesus would have no one to address in this warning. But He does address someone. The text assumes they are there.</td>
                  </tr>
                  <tr className="border-t border-amber-200">
                    <td className="p-3 font-semibold align-top">VERB<br/>(What does it do?)</td>
                    <td className="p-3 align-top italic">"I come as a thief" — an unexpected, sudden arrival. The coming-as-a-thief language is always addressed to people who might be caught unprepared.</td>
                    <td className="p-3 align-top">The verb is coming — present and imminent, right now in the narrative. Jesus is not describing something that happened seven years ago in the story. He is saying it is about to happen, here, at this moment.</td>
                  </tr>
                  <tr className="border-t border-amber-200">
                    <td className="p-3 font-semibold align-top">OBJECT / TIMELINE<br/>(When? Under what condition?)</td>
                    <td className="p-3 align-top italic">"Blessed is he that watcheth" — present tense, active watching, at this specific moment in Revelation 16, between the sixth and seventh bowls, at the gathering of Armageddon.</td>
                    <td className="p-3 align-top">The timing is unmistakable: this verse is placed inside Revelation 16, at the end of the tribulation. Jesus places a rapture warning inside the end of the tribulation. His people are still here — watching, enduring, keeping their garments. The gathering is about to happen.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 bg-white border-2 border-amber-400 rounded-lg p-4">
              <h4 className="text-center font-bold text-amber-800 mb-2 flex items-center justify-center gap-2">
                <span className="text-xl">✦</span>
                VERDICT
                <span className="text-xl">✦</span>
              </h4>
              <p className="text-center text-gray-800">
                Jesus himself places a coming-as-a-thief warning inside the Armageddon sequence — at the end of the tribulation, not before it. He addresses watching believers present on earth at the climax of the age. Once you see it, you cannot unsee it.
              </p>
            </div>
          </div>
        </div>
      </div>

    </article>
  );
};

export default Chapter4Content;
